import { z } from 'zod';
import type {
  BaseParameterDetailAddition,
  BaseSessionDetailAddition,
  BaseSqlDetailAddition,
} from '../types';
import { nullishAsUndefined, nullishToEmpty } from './utils';

export const sessionDetailSchema = z.object({
  pid: z.number({ required_error: 'PID', invalid_type_error: 'PID' }),
  query: z.number({ required_error: 'Query', invalid_type_error: 'Query' }),
  instanceId: z.string({ required_error: 'Instance ID' }).min(1, 'Instance ID'),
  fromTime: nullishAsUndefined(z.string().optional()),
  toTime: nullishAsUndefined(z.string().optional()),
  isOpenedFromPA: nullishAsUndefined(z.boolean().optional()),
  init: nullishAsUndefined(z.boolean().optional()),
});

export const sqlDetailSchema = z
  .object({
    sqlId: nullishAsUndefined(z.string().optional()),
    normalizedSqlId: nullishAsUndefined(z.string().optional()),
    instanceId: z.string({ required_error: 'Instance ID' }).min(1, 'Instance ID'),
    fromTime: nullishAsUndefined(z.string().optional()),
    toTime: nullishAsUndefined(z.string().optional()),
    xmSqlId: nullishAsUndefined(z.string().optional()),
    query: z.preprocess(
      (val) => (val === '' ? null : val),
      nullishAsUndefined(z.coerce.number().optional()),
    ),
  })
  .transform(({ sqlId, normalizedSqlId, ...rest }) => ({
    ...rest,
    sqlId: sqlId ?? normalizedSqlId,
  }))
  .pipe(
    z.object({
      sqlId: z.string({ required_error: 'SQL ID' }).min(1, 'SQL ID'),
      instanceId: z.string().min(1, 'Instance ID'),
      fromTime: nullishAsUndefined(z.string().optional()),
      toTime: nullishAsUndefined(z.string().optional()),
      xmSqlId: nullishAsUndefined(z.string().optional()),
      query: nullishAsUndefined(z.coerce.number().optional()),
    }),
  );

export const parameterDetailSchema = z
  .object({
    instanceId: z.string({ required_error: 'Instance ID' }).min(1, 'Instance ID'),
    name: nullishAsUndefined(z.string().optional()),
    parameterName: nullishAsUndefined(z.string().optional()),
    fromTime: nullishAsUndefined(z.string().optional()),
    toTime: nullishAsUndefined(z.string().optional()),
  })
  .transform(({ name, parameterName, ...rest }) => ({
    ...rest,
    parameterName: name ?? parameterName,
  }))
  .pipe(
    z.object({
      parameterName: z.string({ required_error: 'Parameter Name' }).min(1, 'Parameter Name'),
      instanceId: z.string().min(1, 'Instance ID'),
      fromTime: z.string().optional(),
      toTime: z.string().optional(),
    }),
  );

export const documentTextSchema = z
  .object({
    sqlId: nullishAsUndefined(z.string().optional()),
    normalizedSqlId: nullishAsUndefined(z.string().optional()),
    instanceId: z.preprocess(
      nullishToEmpty(''),
      z.string({ required_error: 'Instance ID' }).min(1, 'Instance ID'),
    ),
    xmSqlId: nullishAsUndefined(z.string().optional()),
  })
  .transform(({ sqlId, normalizedSqlId, ...rest }) => ({
    ...rest,
    sqlId: sqlId ?? normalizedSqlId,
  }))
  .pipe(
    z.object({
      sqlId: z.preprocess(
        nullishToEmpty(''),
        z.string({ required_error: 'SQL ID' }).min(1, 'SQL ID'),
      ),
      instanceId: z.string().min(1, 'Instance ID'),
      xmSqlId: nullishAsUndefined(z.string().optional()),
    }),
  );

export type SessionDetailSchema = z.infer<typeof sessionDetailSchema>;
export type SqlDetailSchema = z.infer<typeof sqlDetailSchema>;
export type ParameterDetailSchema = z.infer<typeof parameterDetailSchema>;
export type DocumentTextSchema = z.infer<typeof documentTextSchema>;

export type RedshiftSessionDetailAddition = BaseSessionDetailAddition & SessionDetailSchema;
export type RedshiftSqlDetailAddition = BaseSqlDetailAddition &
  SqlDetailSchema & {
    database?: string;
    userName?: string;
    userId?: number;
  };
export type RedshiftParameterDetailAddition = Omit<
  BaseParameterDetailAddition & ParameterDetailSchema,
  'parameterName'
>;
